import React, { useCallback, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { SearchBar } from '@/components/common/SearchBar';
import { OverlayPortal } from '@/components/common/OverlayPortal';
import { useAnchoredPosition } from '@/hooks/useAnchoredPosition';
import { useGlobalSearch, type SearchResultItem } from '@/hooks/useGlobalSearch';
import { useTenant } from '@core/tenant/TenantContext';

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const ResultsPanel = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  width: ${({ $width }) => $width}px;
  max-height: 60vh;
  overflow-y: auto;
  z-index: ${({ theme }) => theme.zIndex.dropdown};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  padding: ${({ theme }) => theme.spacing.xs} 0;
`;

const GroupLabel = styled.div`
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.bold};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const ResultRow = styled.button<{ $highlighted: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  text-align: left;
  border: none;
  background: ${({ theme, $highlighted }) => ($highlighted ? theme.colors.surfaceHover : 'transparent')};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.surfaceHover};
  }
`;

const ResultTitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
`;

const ResultSubtitle = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StatusRow = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;

interface FlatResult {
  groupType: string;
  item: SearchResultItem;
}

export const GlobalSearch: React.FC = () => {
  const { inputValue, setInputValue, hasQuery, isLoading, groups } = useGlobalSearch();
  const { isAllMode, availableTenantIds, setSelectedTenantId } = useTenant();
  const navigate = useNavigate();

  const [isOpen, setIsOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const position = useAnchoredPosition(anchorRef, 'bottom', isOpen, 6, 'start', panelRef);

  // Tracked in state via ResizeObserver rather than read from the ref during
  // render: a render-phase getBoundingClientRect() is an impure read that
  // returns the 320px fallback on the first open (the ref is still null) and
  // never updates on resize, so the panel could sit at the wrong width.
  const [anchorWidth, setAnchorWidth] = useState(320);
  React.useEffect(() => {
    const el = anchorRef.current;
    if (!el) return;
    const sync = () => setAnchorWidth(el.getBoundingClientRect().width);
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const flatResults: FlatResult[] = useMemo(
    () => groups.flatMap((g) => g.results.map((item) => ({ groupType: g.type, item }))),
    [groups],
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightIndex(-1);
  }, []);

  const selectResult = useCallback(
    (result: SearchResultItem) => {
      // Owner "All" mode (D9): jump into the result's own tenant first,
      // otherwise the deep link 404s against the currently-selected tenant.
      if (isAllMode && availableTenantIds.includes(result.tenant_id)) {
        setSelectedTenantId(result.tenant_id);
      }
      close();
      setInputValue('');
      navigate(result.path);
    },
    [isAllMode, availableTenantIds, setSelectedTenantId, close, setInputValue, navigate],
  );

  const handleFocus = () => {
    if (hasQuery) setIsOpen(true);
  };

  const handleChange = (value: string) => {
    setInputValue(value);
    setIsOpen(true);
    setHighlightIndex(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) return;
    // Escape and Tab must work even with zero results — otherwise the
    // "No results for …" panel could only be dismissed by clicking away.
    if (e.key === 'Escape' || e.key === 'Tab') {
      close();
      return;
    }
    if (flatResults.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % flatResults.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i <= 0 ? flatResults.length - 1 : i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const chosen = flatResults[highlightIndex] ?? flatResults[0];
      if (chosen) selectResult(chosen.item);
    }
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (wrapperRef.current?.contains(target)) return;
      close();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, close]);

  let flatIdx = -1;

  return (
    <Wrapper ref={wrapperRef} data-testid="candeza-global-search">
      <div ref={anchorRef}>
        <SearchBar
          placeholder="Search students, staff, invoices, packages, subjects…"
          value={inputValue}
          onSearch={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          autoComplete="off"
        />
      </div>
      {isOpen && hasQuery && (
        <OverlayPortal>
          <ResultsPanel
            ref={panelRef}
            role="listbox"
            $top={position.top}
            $left={position.left}
            $width={anchorWidth}
          >
            {isLoading && flatResults.length === 0 && <StatusRow>Searching…</StatusRow>}
            {!isLoading && flatResults.length === 0 && <StatusRow>No results for "{inputValue}"</StatusRow>}
            {groups.map((group) =>
              group.results.length === 0 ? null : (
                <React.Fragment key={group.type}>
                  <GroupLabel data-testid={`candeza-search-group-${group.type}`}>
                    {group.label}
                  </GroupLabel>
                  {group.results.map((item) => {
                    flatIdx += 1;
                    const idx = flatIdx;
                    return (
                      <ResultRow
                        key={`${group.type}-${item.id}`}
                        type="button"
                        role="option"
                        aria-selected={idx === highlightIndex}
                        $highlighted={idx === highlightIndex}
                        onMouseEnter={() => setHighlightIndex(idx)}
                        onClick={() => selectResult(item)}
                        data-testid={`candeza-search-result-${group.type}`}
                      >
                        <ResultTitle>{item.title}</ResultTitle>
                        {item.subtitle && <ResultSubtitle>{item.subtitle}</ResultSubtitle>}
                      </ResultRow>
                    );
                  })}
                </React.Fragment>
              ),
            )}
          </ResultsPanel>
        </OverlayPortal>
      )}
    </Wrapper>
  );
};
